import { API_DOMAIN } from "@monorepo/constants";
import {
  ICalculateAmountPayload,
  ICalculateAmountResponse,
  IFindServiceProviderPayload,
  IGetUpdateServiceInformationPayload,
  IService,
  IServiceGroup,
  ServiceCategoryIdEnum,
} from "@monorepo/types";
import { createApi } from "@reduxjs/toolkit/query/react";

import { customBaseQuery } from "../../helpers/baseQuery";

export const servicesApi = createApi({
  reducerPath: "servicesApi",
  baseQuery: customBaseQuery<{ data: Array<IService> }>({
    baseUrl: API_DOMAIN,
  }),
  tagTypes: ["Services", "Amount"],
  endpoints: (builder) => ({
    services: builder.query<Array<IService>, void>({
      query: () => ({
        url: "/api/v1/services",
      }),
      providesTags: ["Services"],
    }),

    serviceById: builder.query<IService, string>({
      query: (id) => ({
        url: `/api/v1/services/${id}`,
        method: "GET",
      }),
    }),

    serviceGroups: builder.query<Array<IServiceGroup>, string>({
      query: (group) => ({
        url: `/api/v1/services/group/${group}`,
      }),
    }),

    servicesByCategory: builder.query<Array<IService>, ServiceCategoryIdEnum | undefined>({
      query: (categoryId) => ({
        url: `/api/v1/services/category/${categoryId}`,
        method: "GET",
      }),
    }),

    calculateAmount: builder.mutation<ICalculateAmountResponse, ICalculateAmountPayload>({
      query: (payload) => ({
        url: "/api/v1/services/calculate-amount",
        body: payload,
        method: "POST",
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return ["Amount"];
        }
        return [];
      },
    }),

    getUpdateServiceInformation: builder.query<IService, IGetUpdateServiceInformationPayload>({
      query: (params) => ({
        url: "/api/v1/services/update-service-parameters",
        method: "GET",
        params,
      }),
    }),

    scanPdfDocument: builder.mutation<IService, FormData>({
      query: (payload) => ({
        url: "/api/v1/services/scan-pdf-document",
        method: "POST",
        headers: { "content-type": "multipart/form-data" },
        body: payload,
      }),
    }),
    scanBarcode: builder.query<IService, string>({
      query: (barcode) => ({
        url: `/api/v1/services/scan-barcode?barcode=${barcode}`,
      }),
    }),
    findServiceProvider: builder.query<IService, IFindServiceProviderPayload>({
      query: (params) => ({
        url: "/api/v1/services/find",
        method: "GET",
        params,
      }),
    }),
  }),
});

export const {
  useCalculateAmountMutation,
  useServicesQuery,
  useServicesByCategoryQuery,
  useServiceByIdQuery,
  useServiceGroupsQuery,
  useLazyGetUpdateServiceInformationQuery,
  useGetUpdateServiceInformationQuery,
  useScanPdfDocumentMutation,
  useScanBarcodeQuery,
  useLazyFindServiceProviderQuery,
} = servicesApi;

export default servicesApi.reducer;
